import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormHelperText,
  Box,
  IconButton,
  Typography,
  Divider,
  ListSubheader,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { instance } from "../../../axios";
import { store } from "../../../store";
import {
  editUser,
  getUsers,
  deleteUser,
  resetPassword as resetPasswordAction,
} from "../../../redux/actions/user";
import { getStudies, getStudiesBySponsor } from "../../../redux/actions/study";
import { getCenters } from "../../../redux/actions/center";
import { getSponsors } from "../../../redux/actions/sponsor";
import {
  showSuccessAlert,
  showErrorAlert,
  showLoadingAlert,
  closeLoadingAlert,
} from "../utils/AlertUtils";

const formatFirstname = (value) =>
  value
    .toLowerCase()
    .replace(/(^|[\s-])(\S)/g, (match, prefix, letter) =>
      `${prefix}${letter.toUpperCase()}`,
    );

// Organization / role map (shared behavior copied from organize-details)
export const organizationOptions = ["CDL", "Sponsor", "Center"];

export const ROLE_MAP = {
  CDL: [
    { value: "admin", label: "Admin" },
    { value: "technician", label: "Technician" },
    { value: "employee", label: "Employee" },
  ],
  Sponsor: [
    { value: "manager", label: "Manager" },
    { value: "monitor", label: "Monitor" },
  ],
  Center: [{ value: "manager", label: "Manager" }],
};

export const getRoleOptions = (org) => ROLE_MAP[org] || [];

// static list of selects to lock (module-level, not a prop)
const LOCKED_SELECTS = [];
const isLocked = (field) => LOCKED_SELECTS.includes(field);

const EditUserDialog = ({ open, onClose, selectedUser }) => {
  const { t } = useTranslation();

  const studies = useSelector((s) => s.study.studies) || [];
  const centers = useSelector((s) => s.center.centers) || [];
  const sponsors = useSelector((s) => s.sponsor.sponsors) || [];

  const [name, setName] = React.useState("");
  const [firstname, setFirstname] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [isPasswordTouched, setIsPasswordTouched] = React.useState(false);

  const [organization, setOrganization] = React.useState("");
  const [role, setRole] = React.useState("");
  const [sponsorId, setSponsorId] = React.useState("");
  const [studyId, setStudyId] = React.useState("");
  const [centerId, setCenterId] = React.useState("");
  const [checkedStudies, setCheckedStudies] = React.useState([]);
  const [checkedCenters, setCheckedCenters] = React.useState([]);
  const [managerAssignments, setManagerAssignments] = React.useState([
    { studyId: "", centerIds: [] },
  ]);
  const [managerCentersByStudy, setManagerCentersByStudy] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [errorMessage, setErrorMessage] = React.useState("");
  const isLoading = useSelector((state) => state.user.isLoadingUser);

  const isCenterManager = organization === "Center" && role === "manager";
  const isSponsorMonitor = organization === "Sponsor" && role === "monitor";
  const isDynamicStudyCenter = isCenterManager || isSponsorMonitor;

  const sectionCardSx = React.useMemo(
    () => ({
      border: "1px solid",
      borderColor: "grey.100",
      borderRadius: 2,
      bgcolor: "grey.50",
      p: 2.5,
    }),
    [],
  );

  const placeholderTextSx = React.useMemo(
    () => ({
      color: "text.disabled",
      fontStyle: "italic",
    }),
    [],
  );

  const normalizedManagerAssignments = React.useMemo(
    () =>
      (managerAssignments || [])
        .filter((row) => row.studyId !== "" && row.studyId !== null)
        .map((row) => ({
          studyId: Number(row.studyId),
          centerIds: [
            ...new Set((row.centerIds || []).map((id) => Number(id))),
          ],
        })),
    [managerAssignments],
  );

  const managerStudyIds = React.useMemo(
    () => [...new Set(normalizedManagerAssignments.map((row) => row.studyId))],
    [normalizedManagerAssignments],
  );

  const managerCenterIds = React.useMemo(
    () => [
      ...new Set(
        normalizedManagerAssignments.flatMap((row) => row.centerIds || []),
      ),
    ],
    [normalizedManagerAssignments],
  );

  const resolveCenterCountry = React.useCallback((center) => {
    return (
      center?.country?.name ||
      center?.countryName ||
      center?.country ||
      center?.Country?.name ||
      ""
    );
  }, []);

  const buildCenterOptions = React.useCallback(
    (rowCenters, row, rowPrefix, rowIndex) => {
      const hasCountryInfo = (rowCenters || []).some((center) =>
        Boolean(resolveCenterCountry(center)),
      );

      if (!hasCountryInfo) {
        return (rowCenters || []).map((center) => (
          <MenuItem key={`${rowPrefix}-center-${center.id}`} value={center.id}>
            <Checkbox checked={(row.centerIds || []).includes(center.id)} />
            <span>{center.centerNumber}</span>
          </MenuItem>
        ));
      }

      const grouped = (rowCenters || []).reduce((acc, center) => {
        const country = resolveCenterCountry(center) || "Other";
        if (!acc[country]) {
          acc[country] = [];
        }
        acc[country].push(center);
        return acc;
      }, {});

      const groups = Object.entries(grouped);
      const items = [];

      groups.forEach(([country, centersInCountry], countryIndex) => {
        items.push(
          <ListSubheader
            key={`${rowPrefix}-country-${rowIndex}-${country}`}
            disableSticky>
            {country}
          </ListSubheader>,
        );

        centersInCountry.forEach((center) => {
          items.push(
            <MenuItem
              key={`${rowPrefix}-center-${country}-${center.id}`}
              value={center.id}>
              <Checkbox checked={(row.centerIds || []).includes(center.id)} />
              <span>{center.centerNumber}</span>
            </MenuItem>,
          );
        });

        if (countryIndex < groups.length - 1) {
          items.push(
            <Divider
              key={`${rowPrefix}-country-divider-${rowIndex}-${country}`}
            />,
          );
        }
      });

      return items;
    },
    [resolveCenterCountry],
  );

  const renderDynamicStudyCenterRows = React.useCallback(
    (rowPrefix) =>
      (managerAssignments || []).map((row, index) => {
        const currentStudyId = Number(row.studyId);
        const rowCenters = managerCentersByStudy[currentStudyId] || [];
        const hasSelectedCenters =
          Array.isArray(row.centerIds) && row.centerIds.length > 0;
        const selectedInOtherRows = (managerAssignments || [])
          .filter((_, rowIndex) => rowIndex !== index)
          .map((entry) => Number(entry.studyId))
          .filter((id) => Number.isFinite(id) && id > 0);

        return (
          <Box
            key={`${rowPrefix}-row-${index}`}
            sx={{
              position: "relative",
              border: "1px solid",
              borderColor: "grey.100",
              borderRadius: 2,
              px: 2,
              py: 1.25,
              bgcolor: "grey.50",
              mb: index === (managerAssignments?.length || 0) - 1 ? 0 : 1,
            }}>
            <IconButton
              size="small"
              color="error"
              disabled={(managerAssignments || []).length === 1}
              onClick={() => {
                setManagerAssignments((prev) => {
                  const next = (prev || []).filter(
                    (_, rowIndex) => rowIndex !== index,
                  );
                  return next.length > 0
                    ? next
                    : [{ studyId: "", centerIds: [] }];
                });
                setErrorMessage("");
              }}
              sx={{
                position: "absolute",
                top: -10,
                right: -10,
                bgcolor: "rgba(211, 47, 47, 0.10)",
                color: "error.main",
                width: 24,
                height: 24,
                boxShadow: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "error.main",
                  color: "common.white",
                  transform: "scale(1.06)",
                },
                "&.Mui-disabled": {
                  bgcolor: "action.disabledBackground",
                  color: "action.disabled",
                },
              }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id={`${rowPrefix}-study-${index}`}>
                    Study
                  </InputLabel>
                  <Select
                    labelId={`${rowPrefix}-study-${index}`}
                    value={row.studyId}
                    label="Study"
                    onChange={(event) => {
                      const nextStudyId = Number(event.target.value);
                      setManagerAssignments((prev) =>
                        (prev || []).map((entry, rowIndex) =>
                          rowIndex === index
                            ? {
                                studyId: nextStudyId,
                                centerIds: [],
                              }
                            : entry,
                        ),
                      );
                      setErrorMessage("");
                    }}>
                    {studies.map((study) => (
                      <MenuItem
                        key={`${rowPrefix}-study-opt-${study.id}`}
                        value={study.id}
                        disabled={selectedInOtherRows.includes(
                          Number(study.id),
                        )}>
                        {study.cdlStudyCode}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  disabled={!row.studyId}
                  sx={{ position: "relative", pb: 0 }}>
                  {row.studyId && hasSelectedCenters ? (
                    <InputLabel id={`${rowPrefix}-centers-${index}`}>
                      Centers
                    </InputLabel>
                  ) : null}
                  <Select
                    labelId={
                      row.studyId && hasSelectedCenters
                        ? `${rowPrefix}-centers-${index}`
                        : undefined
                    }
                    multiple
                    displayEmpty
                    value={row.centerIds || []}
                    label={
                      row.studyId && hasSelectedCenters ? "Centers" : undefined
                    }
                    onChange={(event) => {
                      const value = event.target.value;
                      const nextCenterIds =
                        typeof value === "string"
                          ? value.split(",").map((id) => Number(id))
                          : value.map((id) => Number(id));

                      setManagerAssignments((prev) =>
                        (prev || []).map((entry, rowIndex) =>
                          rowIndex === index
                            ? {
                                ...entry,
                                centerIds: nextCenterIds,
                              }
                            : entry,
                        ),
                      );
                      setErrorMessage("");
                    }}
                    renderValue={(selected) =>
                      !row.studyId ? (
                        <Typography component="span" sx={placeholderTextSx}>
                          Please select a study first
                        </Typography>
                      ) : !selected || selected.length === 0 ? (
                        <Typography component="span" sx={placeholderTextSx}>
                          Centers
                        </Typography>
                      ) : (
                        (selected || [])
                          .map(
                            (id) =>
                              (rowCenters.find((c) => c.id === id) || {})
                                .centerNumber || id,
                          )
                          .join(", ")
                      )
                    }>
                    {buildCenterOptions(rowCenters, row, rowPrefix, index)}
                  </Select>
                  {row.studyId && rowCenters.length === 0 ? (
                    <FormHelperText
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        mt: 0.5,
                        fontStyle: "italic",
                        whiteSpace: "nowrap",
                      }}>
                      {t("noCenters")}
                    </FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );
      }),
    [
      buildCenterOptions,
      managerAssignments,
      managerCentersByStudy,
      placeholderTextSx,
      studies,
      t,
    ],
  );

  const renderDynamicAddButton = React.useCallback(
    (label) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          width: "100%",
        }}>
        <Button
          variant="outlined"
          sx={{
            height: 56,
            minHeight: 56,
            px: 3,
          }}
          onClick={() => {
            setManagerAssignments((prev) => [
              ...(prev || []),
              { studyId: "", centerIds: [] },
            ]);
            setErrorMessage("");
          }}>
          {label}
        </Button>
      </Box>
    ),
    [],
  );

  const activeStudyId =
    studyId ||
    (organization === "Sponsor" && checkedStudies.length === 1
      ? checkedStudies[0]
      : null);

  React.useEffect(() => {
    if (!isDynamicStudyCenter && role === "monitor" && studyId) {
      setCheckedStudies((prev) => {
        if (prev && prev.length === 1 && String(prev[0]) === String(studyId))
          return prev;
        return [studyId];
      });
    }
  }, [role, studyId, isDynamicStudyCenter]);

  const roleOptions = getRoleOptions(organization);

  const resetForm = React.useCallback(() => {
    setName("");
    setFirstname("");
    setPhone("");
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setIsPasswordTouched(false);
    setOrganization("");
    setRole("");
    setSponsorId("");
    setStudyId("");
    setCenterId("");
    setCheckedStudies([]);
    setCheckedCenters([]);
    setManagerAssignments([{ studyId: "", centerIds: [] }]);
    setManagerCentersByStudy({});
    setErrors({});
    setErrorMessage("");
  }, []);

  const handleClose = React.useCallback(() => {
    try {
      resetForm();
    } finally {
      if (typeof onClose === "function") onClose();
    }
  }, [onClose, resetForm]);

  React.useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }

    if (!selectedUser) {
      resetForm();
      return;
    }

    setName((selectedUser.name || "").toUpperCase());
    setFirstname(formatFirstname(selectedUser.firstname || ""));
    setPhone(selectedUser.phoneNumber || "");
    setEmail((selectedUser.email || "").toLowerCase());
    setPassword("");
    setShowPassword(false);
    setIsPasswordTouched(false);
    setOrganization(selectedUser.organization || "");

    const incomingRole =
      selectedUser.role && typeof selectedUser.role === "string"
        ? selectedUser.role.toLowerCase()
        : "";
    const availableRoles = getRoleOptions(selectedUser.organization || "");
    const initialRole = availableRoles.some((r) => r.value === incomingRole)
      ? incomingRole
      : availableRoles[0]?.value || "";
    setRole(initialRole);

    const isCenterManagerUser =
      selectedUser.organization === "Center" && initialRole === "manager";
    const isSponsorMonitorUser =
      selectedUser.organization === "Sponsor" && initialRole === "monitor";

    setSponsorId(selectedUser.sponsorId || "");

    let initStudyId = selectedUser.studyId || "";
    if (!initStudyId && initialRole === "monitor") {
      if (selectedUser.studies && selectedUser.studies.length === 1) {
        initStudyId = selectedUser.studies[0].id;
      } else if (
        selectedUser.centerMonitors &&
        selectedUser.centerMonitors.length > 0 &&
        selectedUser.centerMonitors[0].studyId
      ) {
        initStudyId = selectedUser.centerMonitors[0].studyId;
      }
    }

    if (
      !initStudyId &&
      selectedUser.organization === "Center" &&
      selectedUser.studies &&
      selectedUser.studies.length > 0
    ) {
      initStudyId = selectedUser.studies[0].id;
    }

    setStudyId(initStudyId || "");
    setCenterId(selectedUser.centerId || "");

    if (isCenterManagerUser || isSponsorMonitorUser) {
      const rowsByStudy = new Map();

      const sourceCenters = isSponsorMonitorUser
        ? selectedUser.centerMonitors || []
        : selectedUser.centers || [];

      if (Array.isArray(sourceCenters) && sourceCenters.length > 0) {
        sourceCenters.forEach((center) => {
          const mappedStudyId = center.studyId || center?.Study?.id || "";
          if (!mappedStudyId) return;

          const numericStudyId = Number(mappedStudyId);
          if (!rowsByStudy.has(numericStudyId)) {
            rowsByStudy.set(numericStudyId, new Set());
          }
          rowsByStudy.get(numericStudyId).add(Number(center.id));
        });
      }

      if (rowsByStudy.size === 0 && selectedUser.studyId) {
        rowsByStudy.set(Number(selectedUser.studyId), new Set());
        if (selectedUser.centerId) {
          rowsByStudy
            .get(Number(selectedUser.studyId))
            .add(Number(selectedUser.centerId));
        }
      }

      if (rowsByStudy.size === 0 && Array.isArray(selectedUser.studies)) {
        selectedUser.studies.forEach((study) => {
          rowsByStudy.set(Number(study.id), new Set());
        });
      }

      const managerRows = Array.from(rowsByStudy.entries()).map(
        ([mappedStudyId, centerSet]) => ({
          studyId: Number(mappedStudyId),
          centerIds: [...centerSet],
        }),
      );

      setManagerAssignments(
        managerRows.length > 0 ? managerRows : [{ studyId: "", centerIds: [] }],
      );
    } else {
      setManagerAssignments([{ studyId: "", centerIds: [] }]);
      setManagerCentersByStudy({});
    }

    setCheckedStudies((prev) => {
      if (prev && prev.length > 0) return prev;
      if (
        (isCenterManagerUser || isSponsorMonitorUser) &&
        Array.isArray(selectedUser.centers)
      ) {
        const studyIdsFromRelations = [
          ...new Set(
            selectedUser.centers
              .map((center) => center.studyId || center?.Study?.id)
              .filter(Boolean),
          ),
        ];

        if (studyIdsFromRelations.length > 0) {
          return studyIdsFromRelations;
        }
      }

      if (selectedUser.studies && selectedUser.studies.length > 0) {
        return selectedUser.studies.map((s) => s.id);
      }

      if (
        (isCenterManagerUser || isSponsorMonitorUser) &&
        selectedUser.centerId
      ) {
        return selectedUser.studyId ? [selectedUser.studyId] : [];
      }

      return selectedUser.checkedStudies || [];
    });

    setCheckedCenters((prev) => {
      if (prev && prev.length > 0) return prev;
      if (
        (isCenterManagerUser || isSponsorMonitorUser) &&
        Array.isArray(selectedUser.centers) &&
        selectedUser.centers.length > 0
      ) {
        return [...new Set(selectedUser.centers.map((center) => center.id))];
      }

      if (
        (isCenterManagerUser || isSponsorMonitorUser) &&
        selectedUser.centerId
      ) {
        return [selectedUser.centerId];
      }

      return selectedUser.centerMonitors &&
        selectedUser.centerMonitors.length > 0
        ? selectedUser.centerMonitors.map((c) => c.id)
        : selectedUser.checkedCenters || [];
    });

    setErrors({});

    if (selectedUser.organization === "Sponsor") {
      store.dispatch(getSponsors());
    }
  }, [open, selectedUser, resetForm]);

  React.useEffect(() => {
    if (!getRoleOptions(organization).some((r) => r.value === role)) {
      setRole(getRoleOptions(organization)[0]?.value || "");
    }
  }, [organization, role]);

  React.useEffect(() => {
    if (organization === "Center" && !role) {
      setRole("manager");
    }
  }, [organization, role]);

  React.useEffect(() => {
    if (organization === "Sponsor" && role === "manager") {
      store.dispatch(getSponsors());
    }
  }, [organization, role]);

  React.useEffect(() => {
    if (!open) return;

    if (organization === "Center") {
      store.dispatch(getStudies());
      return;
    }

    if (isSponsorMonitor) {
      store.dispatch(getStudies());
      return;
    }

    if (sponsorId) {
      store.dispatch(getStudiesBySponsor(sponsorId));
    } else if (!studies || studies.length === 0) {
      store.dispatch(getStudies());
    }
  }, [sponsorId, open, studies.length, organization, isSponsorMonitor]);

  React.useEffect(() => {
    if (!open) return;
    if (isDynamicStudyCenter) return;
    if (activeStudyId) store.dispatch(getCenters(activeStudyId));
  }, [activeStudyId, open, isDynamicStudyCenter]);

  React.useEffect(() => {
    if (!open) return;
    if (!isDynamicStudyCenter) return;

    const selectedStudyIds = managerStudyIds;
    const missingStudyIds = selectedStudyIds.filter(
      (id) => !managerCentersByStudy[id],
    );

    if (missingStudyIds.length === 0) return;

    let isCancelled = false;

    const fetchCentersForStudies = async () => {
      try {
        const responses = await Promise.all(
          missingStudyIds.map(async (id) => {
            const response = await instance.get(`/center/study/${id}`);
            return { studyId: id, centers: response?.data || [] };
          }),
        );

        if (isCancelled) return;

        setManagerCentersByStudy((prev) => {
          const next = { ...prev };
          responses.forEach(({ studyId: currentStudyId, centers: rows }) => {
            next[currentStudyId] = rows.map((center) => ({
              ...center,
              studyId: center.studyId || currentStudyId,
            }));
          });
          return next;
        });
      } catch (error) {
        if (!isCancelled) {
          setManagerCentersByStudy((prev) => ({ ...prev }));
        }
      }
    };

    fetchCentersForStudies();

    return () => {
      isCancelled = true;
    };
  }, [open, isDynamicStudyCenter, managerStudyIds, managerCentersByStudy]);

  React.useEffect(() => {
    if (!isDynamicStudyCenter) return;
    setCheckedStudies(managerStudyIds);
    setCheckedCenters(managerCenterIds);
    setCenterId(managerCenterIds[0] || "");
  }, [isDynamicStudyCenter, managerStudyIds, managerCenterIds]);

  const validateForm = () => {
    const newErrors = {};
    if (!name || !name.trim()) newErrors.name = t("nameRequired");
    if (!firstname || !firstname.trim())
      newErrors.firstname = t("firstnameRequired");
    if (!email || !email.trim()) newErrors.email = t("emailRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSave = async () => {
    if (!selectedUser || !selectedUser.id) return;
    if (!validateForm()) return;

    const passwordToReset =
      isPasswordTouched && password.trim() ? password : "";

    const updates = {
      name,
      firstname,
      email,
      phoneNumber: phone,
      organization,
      role,
      sponsorId: isSponsorMonitor ? null : sponsorId || null,
      studyId: studyId || null,
      centerId: centerId || null,
      checkedStudies,
      checkedCenters,
    };

    if (isDynamicStudyCenter) {
      const normalizedCenterIds = managerCenterIds;
      const primaryCenterId = normalizedCenterIds.includes(Number(centerId))
        ? Number(centerId)
        : normalizedCenterIds[0] || null;
      updates.centerId = primaryCenterId;
      updates.checkedStudies = managerStudyIds;
      updates.checkedCenters = normalizedCenterIds;
    }

    try {
      const result = await store.dispatch(editUser(selectedUser.id, updates));
      if (result?.success) {
        if (passwordToReset) {
          const passwordResetResult = await store.dispatch(
            resetPasswordAction(selectedUser.id, passwordToReset),
          );
          if (!passwordResetResult) {
            const msg = t("passwordResetFailed");
            setErrorMessage(msg);
            showErrorAlert(msg);
            await store.dispatch(getUsers());
            return;
          }
        }

        setErrorMessage("");
        setPassword("");
        setShowPassword(false);
        setIsPasswordTouched(false);
        showSuccessAlert(
          passwordToReset
            ? `${t("userUpdated") || "User updated successfully"} - ${
                t("passwordResetSent") || "Password reset request sent"
              }`
            : t("userUpdated") || "User updated successfully",
        );
        await store.dispatch(getUsers());
        onClose();
      } else {
        if (result?.status === 409 && result?.code === "EMAIL_ALREADY_EXISTS") {
          setErrors((prev) => ({
            ...prev,
            email: "email already existe",
          }));
          setErrorMessage("email already existe");
          return;
        }
        const msg = t("userUpdateFailed") || "Update failed";
        setErrorMessage(msg);
      }
    } catch (err) {
      setErrorMessage(t("userUpdateFailed") || "Update failed");
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !selectedUser.id) return;
    const passwordToReset = password.trim() ? password : "";

    if (passwordToReset) {
      setShowPassword(true);
      setIsPasswordTouched(true);
    }
    setErrors((prev) => ({ ...prev, password: "" }));

    try {
      onClose(true);
      showLoadingAlert(t("loading"));
      const result = await store.dispatch(
        resetPasswordAction(selectedUser.id, passwordToReset || undefined),
      );
      closeLoadingAlert();
      if (result) {
        setPassword("");
        setShowPassword(false);
        setIsPasswordTouched(false);
        showSuccessAlert(t("passwordResetSent"));
      } else {
        showErrorAlert(t("passwordResetFailed"));
      }
    } catch (err) {
      closeLoadingAlert();
      showErrorAlert(t("passwordResetFailed"));
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || !selectedUser.id) return;
    const confirm = window.confirm(
      t("confirmDeleteUser") || "Are you sure you want to delete this user?",
    );
    if (!confirm) return;
    handleClose();
    try {
      showLoadingAlert("Deleting user...");
      const success = await store.dispatch(deleteUser(selectedUser.id));
      closeLoadingAlert();
      if (success) {
        showSuccessAlert(t("userDeleted") || "User deleted successfully");
        store.dispatch(getUsers());
        handleClose();
      } else {
        showErrorAlert(t("deleteUserFailed") || "Failed to delete user");
      }
    } catch (err) {
      closeLoadingAlert();
      showErrorAlert(t("deleteUserFailed") || "Failed to delete user");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}>
      <DialogTitle sx={{ position: "relative", pt: 3, pb: 2, px: 3 }}>
        {t("editUser")}
        {errorMessage ? (
          <Typography
            variant="body2"
            color="error"
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              whiteSpace: "normal",
              fontSize: "0.95rem",
              px: 2,
              py: 0.5,
              bgcolor: "rgba(255,230,230,0.98)",
              color: "rgba(97,26,21,1)",
              border: "1px solid rgba(211,47,47,0.18)",
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              maxWidth: "80%",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}>
            {errorMessage}
          </Typography>
        ) : null}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box
          component="form"
          noValidate
          sx={{
            "& .MuiInputBase-input::placeholder": {
              fontStyle: "italic",
              opacity: 1,
            },
            "& textarea::placeholder": {
              fontStyle: "italic",
              opacity: 1,
            },
          }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={sectionCardSx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("name")}
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value.toUpperCase());
                        setErrorMessage("");
                      }}
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("firstname")}
                      value={firstname}
                      onChange={(e) => {
                        setFirstname(formatFirstname(e.target.value));
                        setErrorMessage("");
                      }}
                      fullWidth
                      error={!!errors.firstname}
                      helperText={errors.firstname}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("email")}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value.toLowerCase());
                        setErrorMessage("");
                        setErrors((prev) => ({ ...prev, email: "" }));
                      }}
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email}
                      sx={{
                        "& .MuiInputBase-root": {
                          bgcolor: errors.email
                            ? "rgba(244, 67, 54, 0.10)"
                            : "",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("phone")}
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        setErrorMessage("");
                      }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <TextField
                        autoComplete="new-password"
                        name="input-password"
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value);
                          setIsPasswordTouched(true);
                          setErrorMessage("");
                          setErrors((prev) => ({ ...prev, password: "" }));
                        }}
                        id="edit-input-password"
                        label={t("password")}
                        type={showPassword ? "text" : "password"}
                        error={!!errors.password}
                        helperText={errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword
                                    ? "hide password"
                                    : "show password"
                                }
                                onClick={() =>
                                  setShowPassword((prev) => !prev)
                                }
                                edge="end">
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                    }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="warning"
                      sx={{ height: 56 }}
                      onClick={handleResetPassword}>
                      {t("resetPassword") || "Reset Password"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={sectionCardSx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={isLocked("organization")}>
                      <InputLabel id="organization-select">
                        {t("organization")}
                      </InputLabel>
                      <Select
                        labelId="organization-select"
                        value={organization}
                        label={t("organization")}
                        onChange={(e) => {
                          setOrganization(e.target.value);
                          setRole(e.target.value === "Center" ? "manager" : "");
                          setStudyId("");
                          setCheckedCenters([]);
                          setCheckedStudies([]);
                          setManagerAssignments([
                            { studyId: "", centerIds: [] },
                          ]);
                          setManagerCentersByStudy({});
                          setCenterId("");
                          setSponsorId("");
                          setErrorMessage("");
                        }}>
                        <MenuItem value="CDL">{t("cdl")}</MenuItem>
                        <MenuItem value="Sponsor">{t("sponsor")}</MenuItem>
                        <MenuItem value="Center">{t("center")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="role-select">{t("role")}</InputLabel>
                      <Select
                        labelId="role-select"
                        disabled={organization === "Center"}
                        value={role}
                        label={t("role")}
                        onChange={(e) => {
                          setRole(e.target.value);
                          setErrorMessage("");
                        }}>
                        {roleOptions.map((r) => (
                          <MenuItem key={r.value} value={r.value}>
                            {t(r.value) || r.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {organization === "Sponsor" && role !== "monitor" && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={isLocked("sponsor")}>
                        <InputLabel id="sponsor-select">
                          {t("sponsor")}
                        </InputLabel>
                        <Select
                          labelId="sponsor-select"
                          value={sponsorId}
                          label={t("sponsor")}
                          onChange={(e) => {
                            setSponsorId(e.target.value);
                            setStudyId("");
                            setCheckedCenters([]);
                            setCheckedStudies([]);
                            setManagerAssignments([
                              { studyId: "", centerIds: [] },
                            ]);
                            setManagerCentersByStudy({});
                            setErrorMessage("");
                          }}>
                          {sponsors.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {organization === "Sponsor" && role !== "monitor" && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="studies-multi">
                          {t("studies")}
                        </InputLabel>
                        <Select
                          labelId="studies-multi"
                          multiple
                          value={checkedStudies}
                          label={t("studies")}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCheckedStudies(
                              typeof value === "string"
                                ? value.split(",")
                                : value,
                            );
                            setErrorMessage("");
                          }}
                          renderValue={(selected) =>
                            (selected || [])
                              .map(
                                (id) =>
                                  (studies.find((s) => s.id === id) || {})
                                    .cdlStudyCode || id,
                              )
                              .join(", ")
                          }>
                          {studies.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              <Checkbox
                                checked={checkedStudies.includes(s.id)}
                              />
                              <span>{s.cdlStudyCode}</span>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {organization === "CDL" && role === "employee" && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel id="edit-cdl-employee-studies-multi">
                          {t("studies")}
                        </InputLabel>
                        <Select
                          labelId="edit-cdl-employee-studies-multi"
                          multiple
                          value={checkedStudies}
                          label={t("studies")}
                          onChange={(event) => {
                            const value = event.target.value;
                            setCheckedStudies(
                              typeof value === "string"
                                ? value.split(",").map((id) => Number(id))
                                : value.map((id) => Number(id)),
                            );
                            setErrorMessage("");
                          }}
                          renderValue={(selected) =>
                            (selected || [])
                              .map(
                                (id) =>
                                  (
                                    studies.find((study) => study.id === id) ||
                                    {}
                                  ).cdlStudyCode || id,
                              )
                              .join(", ")
                          }>
                          {studies.map((study) => (
                            <MenuItem key={study.id} value={study.id}>
                              <Checkbox
                                checked={checkedStudies.includes(study.id)}
                              />
                              <span>{study.cdlStudyCode}</span>
                            </MenuItem>
                          ))}
                        </Select>
                        {!studies || studies.length === 0 ? (
                          <FormHelperText>{t("noStudies")}</FormHelperText>
                        ) : null}
                      </FormControl>
                    </Grid>
                  )}

                  {isDynamicStudyCenter && (
                    <Grid item xs={12}>
                      {renderDynamicAddButton("+ Add Study & Center")}
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>

            {organization === "Center" && !isCenterManager && (
              <Grid item xs={12}>
                <Box sx={sectionCardSx}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="study-select">{t("study")}</InputLabel>
                        <Select
                          labelId="study-select"
                          value={studyId}
                          label={t("study")}
                          onChange={(e) => {
                            setStudyId(e.target.value);
                            setCenterId("");
                            setErrorMessage("");
                          }}>
                          {studies.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.cdlStudyCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        disabled={!centers || centers.length === 0}>
                        <InputLabel id="center-select">
                          {t("centerNumber")}
                        </InputLabel>
                        <Select
                          labelId="center-select"
                          value={centerId}
                          label={t("centerNumber")}
                          onChange={(e) => {
                            setCenterId(e.target.value);
                            setErrorMessage("");
                          }}>
                          {centers.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.centerNumber}
                            </MenuItem>
                          ))}
                        </Select>
                        {!centers || centers.length === 0 ? (
                          <FormHelperText>
                            {t("selectStudyFirst")}
                          </FormHelperText>
                        ) : null}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}

            {organization === "Center" && isCenterManager && (
              <Grid item xs={12}>
                {renderDynamicStudyCenterRows("dynamic")}
              </Grid>
            )}

            {organization === "Sponsor" && role === "monitor" && (
              <Grid item xs={12}>
                {renderDynamicStudyCenterRows("dynamic")}
              </Grid>
            )}

            {!isSponsorMonitor && role === "monitor" && (
              <Grid item xs={12}>
                <Box sx={sectionCardSx}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={!activeStudyId}>
                        <InputLabel id="centers-multi">
                          {t("centers") || "Centers"}
                        </InputLabel>
                        <Select
                          labelId="centers-multi"
                          multiple
                          value={checkedCenters}
                          label={t("centers") || "Centers"}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCheckedCenters(
                              typeof value === "string"
                                ? value.split(",")
                                : value,
                            );
                            setErrorMessage("");
                          }}
                          renderValue={(selected) =>
                            (selected || [])
                              .map(
                                (id) =>
                                  (centers.find((c) => c.id === id) || {})
                                    .centerNumber || id,
                              )
                              .join(", ")
                          }>
                          {centers.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              <Checkbox
                                checked={checkedCenters.includes(c.id)}
                              />
                              <span>{c.centerNumber}</span>
                            </MenuItem>
                          ))}
                        </Select>
                        {!activeStudyId ? (
                          <FormHelperText>
                            {t("selectStudyFirst") ||
                              "Select a study first to choose centers"}
                          </FormHelperText>
                        ) : !centers || centers.length === 0 ? (
                          <FormHelperText>{t("noCenters")}</FormHelperText>
                        ) : null}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "flex-start",
            flex: 1,
          }}>
          {selectedUser?.role !== "admin" && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteUser}
              sx={{ px: 2.5, py: 1.75 }}>
              {t("delete") || "Delete"}
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ px: 2.5, py: 1.75, minWidth: 120 }}>
            {t("cancel")}
          </Button>
          <Button
            onClick={onSave}
            variant="contained"
            disabled={isLoading}
            sx={{ px: 2.5, py: 1.75, minWidth: 120 }}>
            {t("save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
